import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, Collapse, LinearProgress, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { createResumeUploadMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';

import { RESUME_BUCKET, resumeObjectUrl } from '@components/Common/resumeUrl';
import { uploadToS3Direct } from '@components/Common/directUpload';
import {
  partitionResumeFiles,
  rejectReasonLabel,
  RESUME_MAX_BYTES,
  RESUME_MAX_COUNT,
  type RejectedFile,
} from '../selectFiles';
import './index.scss';

type ItemStatus = 'queued' | 'uploading' | 'saving' | 'done' | 'failed';

interface UploadItem {
  id: string;
  file: File;
  status: ItemStatus;
  error?: string;
}

/** How many resumes upload at once. Direct-to-S3, so a handful in flight is comfortable and keeps
 *  the run from feeling sequential without hammering the browser's connection pool. */
const CONCURRENCY = 4;

const humanSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Upload up to fifty résumés in one go, each straight to S3.
 *
 * The bytes never pass through our server: for every file the component asks the BFF for a
 * presigned POST (which validates type and size first), POSTs the file to S3 directly, then records
 * the row. That is the whole reason this exists rather than reusing the multipart route -- fifty
 * CVs through the container is fifty multipart bodies and the Caddy cap; fifty presigned POSTs is
 * none.
 *
 * The interaction is the point too: each file is a row that moves queued -> uploading -> saving ->
 * done|failed on its own, a batch bar fills as they land, and the files that were set aside say why.
 */
const BulkResumeUpload: React.FC<{ disabled?: boolean; onComplete?: () => void }> = ({
  disabled = false,
  onComplete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const [running, setRunning] = useState(false);

  const done = items.filter((i) => i.status === 'done').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const total = items.length;
  const finished = total > 0 && done + failed === total;
  const pct = total === 0 ? 0 : Math.round(((done + failed) / total) * 100);

  const setStatus = useCallback((id: string, status: ItemStatus, error?: string) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status, error } : it)),
    );
  }, []);

  /** Presign, PUT to S3, record the row. Throws on any step so the row shows failed. */
  const uploadOne = useCallback(
    async (item: UploadItem) => {
      setStatus(item.id, 'uploading');
      // `external_<ts>_` marks an imported résumé and keeps two same-named CVs apart; the server
      // sanitises it to a basename and signs a form only for that exact key. maxBytes is the
      // feature's 5MB cap, which the route clamps to the bucket ceiling and S3 then enforces.
      const bucketFileName = `external_${Date.now()}_${item.file.name}`;
      const key = await uploadToS3Direct(RESUME_BUCKET, item.file, {
        fileName: bucketFileName,
        maxBytes: RESUME_MAX_BYTES,
      });

      setStatus(item.id, 'saving');
      await fetchGraphQLApiData(
        createResumeUploadMutation({
          fileName: item.file.name,
          fileType: item.file.type || '',
          fileSize: item.file.size,
          fileUrl: resumeObjectUrl(key),
          bucketKey: key,
          contentHash: '',
          createdBy: 'ui',
        }),
      );
      setStatus(item.id, 'done');
    },
    [setStatus],
  );

  /** A small fixed-size worker pool over the queued items. */
  const runPool = useCallback(
    async (queue: UploadItem[]) => {
      setRunning(true);
      let cursor = 0;
      const worker = async () => {
        while (cursor < queue.length) {
          const item = queue[cursor++];
          try {
            await uploadOne(item);
          } catch (err) {
            const msg = axios.isAxiosError(err)
              ? err.response?.data?.error || err.message
              : (err as Error)?.message;
            setStatus(item.id, 'failed', msg);
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker),
      );
      setRunning(false);
      onComplete?.();
    },
    [uploadOne, setStatus, onComplete],
  );

  const onPick = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const { accepted, rejected: skipped } = partitionResumeFiles(
        Array.from(fileList),
      );
      setRejected(skipped);
      const queued: UploadItem[] = accepted.map((file, i) => ({
        id: `${Date.now()}-${i}-${file.name}`,
        file,
        status: 'queued',
      }));
      setItems(queued);
      if (queued.length) void runPool(queued);
    },
    [runPool],
  );

  const reset = useCallback(() => {
    setItems([]);
    setRejected([]);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const rejectedNote = useMemo(() => {
    if (!rejected.length) return null;
    const shown = rejected.slice(0, 4);
    const more = rejected.length - shown.length;
    return (
      shown
        .map((r) => `${r.name} (${rejectReasonLabel(r.reason, RESUME_MAX_BYTES)})`)
        .join(', ') + (more > 0 ? `, and ${more} more` : '')
    );
  }, [rejected]);

  return (
    <Box className="cc-bulk-resume">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx"
        multiple
        hidden
        onChange={(e) => onPick(e.target.files)}
      />

      <Button
        variant="outlined"
        startIcon={<UploadFileOutlinedIcon />}
        disabled={disabled || running}
        onClick={() => inputRef.current?.click()}
      >
        Select up to {RESUME_MAX_COUNT} resumes
      </Button>
      <Typography className="cc-bulk-resume__hint">
        PDF or DOCX · up to {RESUME_MAX_COUNT} files ·{' '}
        {Math.round(RESUME_MAX_BYTES / 1024 / 1024)} MB each
      </Typography>

      <Collapse in={total > 0} unmountOnExit>
        <Box className="cc-bulk-resume__panel">
          <Box className="cc-bulk-resume__head">
            <Typography className="cc-bulk-resume__count">
              {finished
                ? `${done} uploaded${failed ? `, ${failed} failed` : ''}`
                : `Uploading ${done + failed} of ${total}…`}
            </Typography>
            {finished && (
              <Button size="small" variant="text" onClick={reset}>
                Add more
              </Button>
            )}
          </Box>

          <LinearProgress
            className={`cc-bulk-resume__bar${finished ? ' is-done' : ''}`}
            variant="determinate"
            value={pct}
          />

          <Box className="cc-bulk-resume__list">
            {items.map((it) => (
              <Box
                key={it.id}
                className={`cc-bulk-resume__row is-${it.status}`}
              >
                <span className="cc-bulk-resume__icon">
                  {it.status === 'done' ? (
                    <CheckCircleRoundedIcon className="ic ic--done" />
                  ) : it.status === 'failed' ? (
                    <ErrorOutlineRoundedIcon className="ic ic--fail" />
                  ) : it.status === 'queued' ? (
                    <CloudUploadOutlinedIcon className="ic ic--idle" />
                  ) : (
                    <span className="cc-bulk-resume__spinner" aria-hidden />
                  )}
                </span>
                <span className="cc-bulk-resume__name" title={it.file.name}>
                  {it.file.name}
                </span>
                <span className="cc-bulk-resume__meta">
                  {it.status === 'failed'
                    ? it.error || 'failed'
                    : it.status === 'uploading'
                      ? 'uploading'
                      : it.status === 'saving'
                        ? 'saving'
                        : humanSize(it.file.size)}
                </span>
              </Box>
            ))}
          </Box>

          {rejectedNote && (
            <Typography className="cc-bulk-resume__skipped">
              Skipped {rejected.length}: {rejectedNote}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default BulkResumeUpload;
